import {
  useGetFailedAttendanceJobsQuery,
  useGetFailedAttendanceRecordsQuery,
  useLazyDownloadAttendanceTemplateQuery,
  useUploadAttendanceMutation,
} from '@/app/features/employee/emp-attendance/emp-attendance.api';
import type { FailedAttendanceRecord } from '@/app/features/employee/emp-attendance/emp-attendance.type';
import { BulkUploader, BulkUploadErrors } from '@/components/BulkUploader';
import useModal from '@/hooks/useModal';
import { toast } from '@/lib/toast';
import { Button, type TableProps } from 'antd';

type Props = {
  open: boolean;
  handleClose: (value?: boolean) => void;
};

const columns: TableProps<FailedAttendanceRecord>['columns'] = [
  {
    width: 130,
    title: 'Row Number',
    dataIndex: 'row_number',
    render: (val) => val ?? 'n/a',
  },
  {
    width: 100,
    title: 'Date',
    dataIndex: 'raw_data',
    render: (val) => val?.['Date (YYYY-MM-DD)'] ?? 'n/a',
  },
  {
    title: 'Email',
    dataIndex: 'email',
    render: (val) => val ?? 'n/a',
  },
  {
    title: 'Failure Reason',
    dataIndex: 'failure_reason',
    render: (val) =>
      val ? (
        <span className="block min-w-[200px] text-red-400">{val}</span>
      ) : (
        'n/a'
      ),
  },
];

export default function BulkAttendanceUpload({ open, handleClose }: Props) {
  const [uploadAttendance, { isLoading: isUploading }] =
    useUploadAttendanceMutation();
  const [downloadTemplate] = useLazyDownloadAttendanceTemplateQuery();

  const {
    open: errorOpen,
    handleOpen: errorHandleOpen,
    handleClose: errorClose,
  } = useModal<undefined, 'ERROR_SHOW'>();

  const handleDownloadTemplate = async () => {
    try {
      const blobUrl = await downloadTemplate().unwrap();
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'attendance-template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast('error', 'Failed to download template.');
    }
  };

  const handleUpload = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    await uploadAttendance(formData).unwrap();
  };

  return (
    <>
      <BulkUploader
        open={open}
        onClose={() => handleClose()}
        title="Attendance CSV Upload"
        subtitle="File Type Must be in .xlsx"
        accept=".xlsx"
        onDownloadTemplate={handleDownloadTemplate}
        onUpload={handleUpload}
        uploadLoading={isUploading}
        errorViewer={
          <Button
            type="default"
            className="!h-10 text-center text-teal-500 text-sm font-normal underline !bg-transparent !border-none !shadow-none"
            onClick={() => errorHandleOpen({ type: 'ERROR_SHOW' })}
          >
            View Error Table Data
          </Button>
        }
      />

      <BulkUploadErrors<FailedAttendanceRecord>
        open={errorOpen.open}
        onClose={errorClose}
        useGetJobsQuery={useGetFailedAttendanceJobsQuery}
        useGetRecordsQuery={useGetFailedAttendanceRecordsQuery}
        columns={columns}
        jobsErrorMessage="Failed to get attendance import error data"
        recordsErrorMessage="Failed to get attendance import error data"
      />
    </>
  );
}
